import React, { useState } from "react";
import { Link } from "gatsby";
import { FiDownload } from "@react-icons/all-files/fi/FiDownload";
import { FiMail } from "@react-icons/all-files/fi/FiMail";
import { FiCalendar } from "@react-icons/all-files/fi/FiCalendar";
import { IoMdArrowForward } from "@react-icons/all-files/io/IoMdArrowForward";
import { PATH } from "~/constant/path";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";

type CtaCard = {
  key: string;
  title: string;
  description: string;
  buttonLabel: string;
  to: string;
  external?: boolean;
  icon: React.ReactNode;
};

const cards: CtaCard[] = [
  {
    key: "request",
    title: "まずは情報を集めたい",
    description: "サービス概要・料金・導入事例をまとめた資料をご覧いただけます。",
    buttonLabel: "資料をダウンロードする",
    to: PATH.REQUEST.to,
    icon: <FiDownload />,
  },
  {
    key: "meeting",
    title: "担当者と直接話したい",
    description: "採用のお悩みや進め方について、オンラインでご相談いただけます。",
    buttonLabel: "オンライン相談を予約する",
    to: PATH.MEETING_BOOKING.websiteTo,
    icon: <FiCalendar />,
  },
  {
    key: "inquiry",
    title: "メールで問い合わせたい",
    description: "ご質問やご相談ごとをフォームからお気軽にお送りいただけます。",
    buttonLabel: "フォームで問い合わせる",
    to: PATH.INQUIRY_CONTACT.to,
    icon: <FiMail />,
  },
];

export const AskUs = () => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  return (
    <>
      <div className="container">
        <span className="bgPhoto" aria-hidden="true" />
        <span className="bgGlow" aria-hidden="true" />
        <div className="body">
          <div className="titleWrapper">
            <h2 className="title">Refcomeをもっと知りたい方へ</h2>
          </div>
          <p className="subText">
            まずは情報収集したい方も、具体的に相談したい方も。
            <br />
            ご検討状況に合わせてお選びください。
          </p>
          <div className="cards">
            {cards.map((card) => {
              const isHovered = hoveredKey === card.key;
              const buttonClass = isHovered ? "askUsCtaButton isHovered" : "askUsCtaButton";

              return (
                <div key={card.key} className="card">
                  <div className="icon" aria-hidden="true">
                    {card.icon}
                  </div>
                  <h3 className="cardTitle">{card.title}</h3>
                  <p className="cardDescription">{card.description}</p>
                  {card.external ? (
                    <a
                      className={buttonClass}
                      href={card.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseEnter={() => setHoveredKey(card.key)}
                      onMouseLeave={() => setHoveredKey(null)}
                    >
                      <span className="ctaLabel">{card.buttonLabel}</span>
                      <span className="ctaArrow" aria-hidden="true">
                        <IoMdArrowForward />
                      </span>
                    </a>
                  ) : (
                    <Link
                      className={buttonClass}
                      to={card.to}
                      onMouseEnter={() => setHoveredKey(card.key)}
                      onMouseLeave={() => setHoveredKey(null)}
                    >
                      <span className="ctaLabel">{card.buttonLabel}</span>
                      <span className="ctaArrow" aria-hidden="true">
                        <IoMdArrowForward />
                      </span>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        .container {
          position: relative;
          display: flex;
          justify-content: center;
          overflow: hidden;
          background: linear-gradient(135deg, #ec7f18 0%, #ec553f 100%);
          padding: 72px 24px;
        }
        .bgPhoto {
          position: absolute;
          inset: 0;
          background-image: url("/hero/hero-photo.jpg");
          background-size: cover;
          background-position: 78% 28%;
          opacity: 0.32;
          mix-blend-mode: soft-light;
          pointer-events: none;
        }
        .bgGlow {
          position: absolute;
          top: -70px;
          right: -60px;
          width: 280px;
          height: 280px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.24) 0%, rgba(255, 255, 255, 0) 70%);
          pointer-events: none;
        }
        .body {
          position: relative;
          width: 1000px;
        }
        .titleWrapper {
          text-align: center;
        }
        .title {
          margin: 0;
          font-size: 40px;
          font-weight: bold;
          color: ${colors.white};
          line-height: 1.35;
          letter-spacing: 0.02em;
        }
        .subText {
          margin: 16px 0 0;
          font-size: 18px;
          color: ${colors.white};
          text-align: center;
          line-height: 1.8;
        }
        .cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 28px;
          margin-top: 36px;
        }
        .card {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          background: ${colors.white};
          border-radius: 16px;
          padding: 32px 24px 28px;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
          text-align: center;
        }
        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 1px solid ${colors.orange};
          background: ${colors.white};
          color: ${colors.orange};
          font-size: 26px;
        }
        .cardTitle {
          margin: 16px 0 0;
          font-size: 22px;
          font-weight: bold;
          color: ${colors.defaultText};
          line-height: 1.4;
        }
        .cardDescription {
          margin: 10px 0 0;
          flex: 1;
          font-size: 15px;
          color: ${colors.lightText};
          line-height: 1.7;
        }
        .ctaLabel {
          display: inline-block;
        }
      `}</style>

      {/*
        GatsbyのLink / 通常のaには styled-jsx のクラスが自動では付かないため、
        見た目とホバーはグローバル指定で確実に当てる。
        クラス名は記事内CTA（.ctaButton）とぶつからないよう askUs 専用にする。
      */}
      <style jsx global>{`
        .askUsCtaButton {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
          width: 100% !important;
          min-height: 52px !important;
          margin-top: 20px !important;
          padding: 14px 18px !important;
          border-radius: 999px !important;
          border: none !important;
          background-image: ${colors.brandGradient} !important;
          color: ${colors.white} !important;
          font-size: 16px !important;
          font-weight: bold !important;
          letter-spacing: 0.02em !important;
          text-decoration: none !important;
          box-shadow: 0 8px 18px rgba(236, 100, 40, 0.3) !important;
          transition: transform 0.25s ease, box-shadow 0.25s ease !important;
        }
        .askUsCtaButton.isHovered {
          transform: translateY(-4px) !important;
          box-shadow: 0 14px 28px rgba(236, 100, 40, 0.4) !important;
        }
        .askUsCtaButton.isHovered .ctaArrow {
          transform: translateX(4px) !important;
        }
        .askUsCtaButton .ctaArrow {
          display: inline-flex !important;
          align-items: center !important;
          transition: transform 0.25s ease !important;
        }
        .askUsCtaButton .ctaArrow svg {
          width: 18px !important;
          height: 18px !important;
        }
        @media (${breakPoints.tablet}) {
          .askUsCtaButton {
            min-height: 48px !important;
            font-size: 15px !important;
          }
        }
      `}</style>

      <style jsx>{`
        @media (${breakPoints.tablet}) {
          .container {
            padding: 48px 16px;
          }
          .title {
            font-size: 28px;
          }
          .subText {
            margin-top: 10px;
            font-size: 15px;
          }
          .cards {
            grid-template-columns: 1fr;
            gap: 18px;
            margin-top: 28px;
            max-width: 460px;
            margin-left: auto;
            margin-right: auto;
          }
          .card {
            padding: 24px 20px 22px;
          }
          .cardTitle {
            font-size: 18px;
          }
          .cardDescription {
            font-size: 14px;
          }
        }
      `}</style>
    </>
  );
};
